import React, { useEffect, useRef, useState } from 'react';
import './MarketCharts.css';

const MarketCharts = () => {
  const [isLoading, setIsLoading] = useState(true);
  const widgetRefs = {
    btc: useRef(null),
    eth: useRef(null),
    bnb: useRef(null),
    ticker: useRef(null)
  };

  useEffect(() => {
    let scriptLoadTimeout;
    let widgetInitTimeout;
    let isComponentMounted = true;

    const loadScript = (src) => {
      return new Promise((resolve, reject) => {
        const script = document.querySelector(`script[src="${src}"]`);
        if (script) {
          resolve();
          return;
        }

        const newScript = document.createElement('script');
        newScript.src = src;
        newScript.async = true;
        newScript.onload = resolve;
        newScript.onerror = reject;
        document.head.appendChild(newScript);
      });
    };

    const initializeWidget = (container, config) => {
      if (!container.current || !window.TradingView) return;
      
      try {
        container.current.innerHTML = '';
        const widgetContainer = document.createElement('div');
        container.current.appendChild(widgetContainer);

        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.innerHTML = JSON.stringify(config);
        
        if (config.type === 'mini-symbol-overview') {
          script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-mini-symbol-overview.js';
        } else if (config.type === 'ticker-tape') {
          script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-ticker-tape.js';
        }

        container.current.appendChild(script);
      } catch (error) {
        console.error('Widget initialization error:', error);
      }
    };

    const initializeWidgets = async () => {
      try {
        await loadScript('https://s3.tradingview.com/tv.js');
        
        if (!isComponentMounted) return;

        // Wait a bit for TradingView to be fully initialized
        await new Promise(resolve => setTimeout(resolve, 500));

        const widgetConfigs = [
          {
            ref: widgetRefs.btc,
            config: {
              type: 'mini-symbol-overview',
              symbol: "BITSTAMP:BTCUSD",
              width: "100%",
              height: "100%",
              locale: "en",
              dateRange: "1D",
              colorTheme: "dark",
              isTransparent: true,
              autosize: true,
              largeChartUrl: "",
              hideDateRanges: false,
              hideMarketStatus: false,
              hideSymbolLogo: false,
              scalePosition: "right",
              scaleMode: "Normal",
              fontFamily: "-apple-system, BlinkMacSystemFont, Trebuchet MS, Roboto, Ubuntu, sans-serif",
              fontSize: "10",
              noTimeScale: false,
              valuesTracking: "1",
              changeMode: "price-and-percent",
              chartOnly: false,
              container_id: "widget-btc"
            }
          },
          {
            ref: widgetRefs.eth,
            config: {
              type: 'mini-symbol-overview',
              symbol: "BINANCE:ETHUSDT",
              width: "100%",
              height: "100%",
              locale: "en",
              dateRange: "1D",
              colorTheme: "dark",
              isTransparent: true,
              autosize: true,
              largeChartUrl: "",
              hideDateRanges: false,
              hideMarketStatus: false,
              hideSymbolLogo: false,
              scalePosition: "right",
              scaleMode: "Normal",
              fontFamily: "-apple-system, BlinkMacSystemFont, Trebuchet MS, Roboto, Ubuntu, sans-serif",
              fontSize: "10",
              noTimeScale: false,
              valuesTracking: "1",
              changeMode: "price-and-percent",
              chartOnly: false,
              container_id: "widget-eth"
            }
          },
          {
            ref: widgetRefs.bnb,
            config: {
              type: 'mini-symbol-overview',
              symbol: "BINANCE:BNBUSDT",
              width: "100%",
              height: "100%",
              locale: "en",
              dateRange: "1D",
              colorTheme: "dark",
              isTransparent: true,
              autosize: true,
              largeChartUrl: "",
              hideDateRanges: false,
              hideMarketStatus: false,
              hideSymbolLogo: false,
              scalePosition: "right",
              scaleMode: "Normal",
              fontFamily: "-apple-system, BlinkMacSystemFont, Trebuchet MS, Roboto, Ubuntu, sans-serif",
              fontSize: "10",
              noTimeScale: false,
              valuesTracking: "1",
              changeMode: "price-and-percent",
              chartOnly: false,
              container_id: "widget-bnb"
            }
          }
        ];

        // Initialize chart widgets
        widgetConfigs.forEach(({ ref, config }) => {
          initializeWidget(ref, config);
        });

        // Initialize ticker
        initializeWidget(widgetRefs.ticker, {
          type: 'ticker-tape',
          symbols: [
            { proName: "BITSTAMP:BTCUSD", title: "BTC/USD" },
            { proName: "BINANCE:ETHUSDT", title: "ETH/USDT" },
            { proName: "BINANCE:BNBUSDT", title: "BNB/USDT" },
            { proName: "BINANCE:DOGEUSDT", title: "DOGE/USDT" },
            { proName: "BINANCE:SOLUSDT", title: "SOL/USDT" }
          ],
          showSymbolLogo: true,
          colorTheme: "dark",
          isTransparent: true,
          displayMode: "adaptive",
          locale: "en"
        });

        if (isComponentMounted) {
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error initializing widgets:', error);
        if (isComponentMounted) {
          setIsLoading(false);
        }
      }
    };

    // Initialize with retry mechanism
    const initWithRetry = async (retries = 3) => {
      for (let i = 0; i < retries; i++) {
        try {
          await initializeWidgets();
          break;
        } catch (error) {
          console.error(`Attempt ${i + 1} failed:`, error);
          if (i === retries - 1) {
            setIsLoading(false);
            break;
          }
          await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
        }
      }
    };

    initWithRetry();

    return () => {
      isComponentMounted = false;
      clearTimeout(scriptLoadTimeout);
      clearTimeout(widgetInitTimeout);
      
      // Cleanup widgets
      Object.values(widgetRefs).forEach(ref => {
        if (ref.current) {
          ref.current.innerHTML = '';
        }
      });
    };
  }, []);

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className={`chart-container rounded-lg shadow-lg p-4 ${isLoading ? 'loading' : ''}`} style={{ background: '#1B3160' }}>
          <h3 className="chart-title text-white">Bitcoin (BTC/USD)</h3>
          <div className="chart-wrapper">
            <div ref={widgetRefs.btc} id="widget-btc" className="tradingview-widget-container" />
          </div>
        </div>
        
        <div className={`chart-container rounded-lg shadow-lg p-4 ${isLoading ? 'loading' : ''}`} style={{ background: '#1B3160' }}>
          <h3 className="chart-title text-white">Ethereum (ETH/USDT)</h3>
          <div className="chart-wrapper">
            <div ref={widgetRefs.eth} id="widget-eth" className="tradingview-widget-container" />
          </div>
        </div>
        
        <div className={`chart-container rounded-lg shadow-lg p-4 ${isLoading ? 'loading' : ''}`} style={{ background: '#1B3160' }}>
          <h3 className="chart-title text-white">Binance Coin (BNB/USDT)</h3>
          <div className="chart-wrapper">
            <div ref={widgetRefs.bnb} id="widget-bnb" className="tradingview-widget-container" />
          </div>
        </div>
      </div>

      <div className="ticker-container">
        <div ref={widgetRefs.ticker} className="ticker-wrapper" />
      </div>
    </>
  );
};

export default MarketCharts; 